import type { ServiceHoursConfig, ServiceHoursDay } from "@kontextwerk/control-web-client"

const WEEKDAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const

const normalizeText = (value?: string | null): string | null => {
  const text = value?.trim()

  return text || null
}

const normalizeTime = (value?: string | null): string | null => {
  const text = normalizeText(value)

  if (!text) return null

  return text.length >= 5 ? text.slice(0, 5) : text
}

const formatDayWindows = (day: ServiceHoursDay): string | null => {
  const windows = (day.windows ?? [])
    .map((window) => {
      const start = normalizeTime(window.start_time)
      const end = normalizeTime(window.end_time)

      if (!start || !end) return null

      return `${start}-${end} Uhr`
    })
    .filter((line): line is string => Boolean(line))

  return windows.length ? windows.join(", ") : null
}

const formatDayRange = (startWeekday: number, endWeekday: number): string => {
  const start = WEEKDAY_LABELS[startWeekday] ?? String(startWeekday)
  const end = WEEKDAY_LABELS[endWeekday] ?? String(endWeekday)

  return startWeekday === endWeekday ? start : `${start}-${end}`
}

const pushServiceHourRange = (
  lines: string[],
  rangeStart: number | null,
  rangeEnd: number | null,
  label: string | null,
) => {
  if (rangeStart === null || rangeEnd === null || !label) return

  lines.push(`${formatDayRange(rangeStart, rangeEnd)} ${label}`)
}

export const formatServiceHoursLines = (serviceHours?: ServiceHoursConfig | null): string[] => {
  if (!serviceHours) return ["Keine öffentlichen Öffnungszeiten hinterlegt."]

  const daysByWeekday = new Map<number, ServiceHoursDay>()
  for (const day of serviceHours?.hours ?? []) {
    if (Number.isInteger(day.weekday) && day.weekday >= 0 && day.weekday <= 6) {
      daysByWeekday.set(day.weekday, day)
    }
  }

  const lines: string[] = []
  let rangeStart: number | null = null
  let rangeEnd: number | null = null
  let activeWindowLabel: string | null = null

  const flushRange = () => {
    pushServiceHourRange(lines, rangeStart, rangeEnd, activeWindowLabel)
  }

  for (let weekday = 0; weekday < WEEKDAY_LABELS.length; weekday += 1) {
    const windowLabel = daysByWeekday.has(weekday)
      ? (formatDayWindows(daysByWeekday.get(weekday)!) ?? "geschlossen")
      : "geschlossen"

    if (windowLabel === activeWindowLabel && rangeEnd !== null && weekday === rangeEnd + 1) {
      rangeEnd = weekday
      continue
    }

    flushRange()
    rangeStart = weekday
    rangeEnd = weekday
    activeWindowLabel = windowLabel
  }
  flushRange()

  return lines
}

export type ServiceHoursPreviewModel = {
  statusTag: string
  statusColor: "green" | "neutral"
  regularLines: string[]
  hasMoreRegularLines: boolean
  closedDatesLine: string
  holidaysLine: string
}

export const buildServiceHoursPreview = (
  serviceHours?: ServiceHoursConfig | null,
  maxRegularLines = 3,
): ServiceHoursPreviewModel => {
  const isConfigured = Boolean(serviceHours)
  const regularLines = formatServiceHoursLines(serviceHours).slice(0, maxRegularLines)
  const allRegularLines = formatServiceHoursLines(serviceHours)
  const closedDateCount = serviceHours?.closed_dates?.length ?? 0
  const holidaysExcluded = serviceHours?.exclude_german_nationwide_holidays === true

  return {
    statusTag: isConfigured ? "Gepflegt" : "Nicht gepflegt",
    statusColor: isConfigured ? "green" : "neutral",
    regularLines,
    hasMoreRegularLines: allRegularLines.length > maxRegularLines,
    closedDatesLine:
      closedDateCount > 0
        ? closedDateCount === 1
          ? "1 ausgeschlossener Tag"
          : `${closedDateCount} ausgeschlossene Tage`
        : "Keine ausgeschlossenen Tage",
    holidaysLine: holidaysExcluded
      ? "Bundesweite Feiertage werden berücksichtigt"
      : "Feiertage werden nicht automatisch ausgeschlossen",
  }
}
